import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnInit,
  OnDestroy,
  computed,
  inject,
  signal,
} from '@angular/core';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { MOBILE_BREAKPOINT } from 'src/app/@core/constants/breakpoints';
import { HlmDialogContentComponent } from '../../spartan/dialog/hlm-dialog-content.component';
import { HlmDialogHeaderComponent } from '../../spartan/dialog/hlm-dialog-header.component';
import { HlmDialogFooterComponent } from '../../spartan/dialog/hlm-dialog-footer.component';
import { HlmDialogTitleDirective } from '../../spartan/dialog/hlm-dialog-title.directive';
import { HlmDialogDescriptionDirective } from '../../spartan/dialog/hlm-dialog-description.directive';

export interface AlertDialogContext {
  title?: string;
  description?: string;
  okText?: string;
  fullscreenOnMobile?: boolean;
}

/**
 * Alert dialog component used internally by DialogService.alert().
 * Dynamically rendered via BrnDialogService.open() — do NOT use directly.
 */
@Component({
  selector: 'app-alert-dialog',
  standalone: true,
  imports: [
    HlmDialogContentComponent,
    HlmDialogHeaderComponent,
    HlmDialogFooterComponent,
    HlmDialogTitleDirective,
    HlmDialogDescriptionDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <hlm-dialog-content [showCloseButton]="false" [class]="contentClass()">
      <hlm-dialog-header>
        <h3 hlmDialogTitle>{{ ctx.title ?? 'Notice' }}</h3>
        @if (ctx.description) {
          <p hlmDialogDescription>{{ ctx.description }}</p>
        }
      </hlm-dialog-header>

      <hlm-dialog-footer class="mt-4">
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          (click)="onOk()"
        >
          {{ ctx.okText ?? 'OK' }}
        </button>
      </hlm-dialog-footer>
    </hlm-dialog-content>
  `,
})
export class AlertDialogComponent implements OnInit, OnDestroy {
  protected readonly ctx = injectBrnDialogContext<AlertDialogContext>();
  private readonly _dialogRef = inject(BrnDialogRef);

  private readonly _windowWidth = signal<number>(
    typeof window !== 'undefined' ? window.innerWidth : 1200,
  );

  readonly isMobile = computed(() => this._windowWidth() < MOBILE_BREAKPOINT);

  readonly contentClass = computed(() => {
    const fullscreen = this.ctx.fullscreenOnMobile ?? true;
    if (fullscreen && this.isMobile()) {
      return '!fixed !inset-0 !w-screen !h-dvh !max-w-none !max-h-none !rounded-none !translate-x-0 !translate-y-0 !top-0 !left-0 flex flex-col';
    }
    return 'w-full max-w-sm';
  });

  @HostListener('window:resize')
  onResize(): void {
    this._windowWidth.set(window.innerWidth);
  }

  ngOnInit(): void {
    this._windowWidth.set(
      typeof window !== 'undefined' ? window.innerWidth : 1200,
    );
  }

  ngOnDestroy(): void {}

  onOk(): void {
    this._dialogRef.close();
  }
}
